import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArchiveIcon, ArrowUpRightIcon, BoxesIcon, CheckIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon, ClipboardListIcon, SlidersHorizontalIcon, XIcon } from 'lucide-react';
import { CarliStars } from './CarliStars';
import { scoreConfidence } from '../utils/recoveryConfidence';
import { reconcileSubmission } from '../utils/submissionOutcome';
import { RecoveryRules, defaultRulesForBrand, rulesMatchDefaults, summarizeRules } from '../data/recoveryRules';
import { Batch, eddyIncomingBatches, onePeopleIncomingBatches, submissionId } from '../data/inventorySubmissions';
import { useNisoloProducts } from '../hooks/useNisoloProducts';
import { useNisoloSubmissions } from '../hooks/useNisoloSubmissions';
import { useNisoloOrders } from '../hooks/useNisoloOrders';
import { UploadAIInsights } from './UploadAIInsights';
import { UnitsDashboard } from './UnitsDashboard';
import { SubmissionRules } from './SubmissionRules';
type SummaryMetric = {
  label: string;
  value: string;
  detail: string;
};
const onePeopleSummaryMetrics: SummaryMetric[] = [{
  label: 'Uploaded products',
  value: '189',
  detail: 'Products'
}, {
  label: 'Available for sale products',
  value: '83',
  detail: 'Products'
}, {
  label: 'Live products',
  value: '80',
  detail: 'Products'
}, {
  label: 'Units uploaded',
  value: '1,902',
  detail: 'Units'
}, {
  label: 'Available for sale units',
  value: '1,024',
  detail: 'Units'
}, {
  label: 'Live units',
  value: '1,019',
  detail: 'Units'
}];
const emptySummaryMetrics: SummaryMetric[] = [{
  label: 'Uploaded products',
  value: '0',
  detail: 'Products'
}, {
  label: 'Available for sale products',
  value: '0',
  detail: 'Products'
}, {
  label: 'Live products',
  value: '0',
  detail: 'Products'
}, {
  label: 'Units uploaded',
  value: '0',
  detail: 'Units'
}, {
  label: 'Available for sale units',
  value: '0',
  detail: 'Units'
}, {
  label: 'Live units',
  value: '0',
  detail: 'Units'
}];
const eddySummaryMetrics: SummaryMetric[] = [{
  label: 'Uploaded products',
  value: '320',
  detail: 'Products'
}, {
  label: 'Available for sale products',
  value: '86',
  detail: 'Products'
}, {
  label: 'Live products',
  value: '82',
  detail: 'Products'
}, {
  label: 'Units uploaded',
  value: '2,981',
  detail: 'Units'
}, {
  label: 'Available for sale units',
  value: '682',
  detail: 'Units'
}, {
  label: 'Live units',
  value: '669',
  detail: 'Units'
}];
type BrandBase = {
  receivedProducts: number;
  receivedUnits: number;
  totalMsrp: number;
  maxOpportunity: number;
  soldUnits: number;
  totalSales: number;
};
type BatchRow = Batch & {
  brand: string;
  id: string;
};
type SortKey = 'uploaded' | 'batch' | 'brand' | 'units' | 'msrp' | 'recovery';
const ROWS_PER_PAGE = 50;

// Submission dates arrive as display strings ("Feb 5, 2026") and can be missing.
function uploadedTime(value: string) {
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
}
const onePeopleBase: BrandBase = {
  receivedProducts: 189,
  receivedUnits: 1902,
  totalMsrp: 130993,
  maxOpportunity: 65437,
  soldUnits: 2920,
  totalSales: 49411
};
const eddyBase: BrandBase = {
  receivedProducts: 320,
  receivedUnits: 2981,
  totalMsrp: 321656,
  maxOpportunity: 160828,
  soldUnits: 4010,
  totalSales: 325875
};
const currency = (value: number) => value.toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});
export function InventoryDashboard({
  selectedOwner,
  isAdmin = false,
  onOpenSettings
}: {
  selectedOwner: string;
  isAdmin?: boolean;
  onOpenSettings?: (submissionId: string) => void;
}) {
  const allBrands = selectedOwner === '';
  const includes = (brand: string) => allBrands || selectedOwner === brand;
  const nisoloActive = includes('Nisolo');
  const {
    inventory: nisolo
  } = useNisoloProducts(nisoloActive);
  const {
    data: nisoloSubmissions,
    loading: submissionsLoading,
    error: submissionsError
  } = useNisoloSubmissions(nisoloActive);
  const {
    data: nisoloOrders
  } = useNisoloOrders(nisoloActive);
  const [section, setSection] = useState<'Submissions' | 'Units'>('Submissions');
  const [deselected, setDeselected] = useState<string[]>([]);
  const [rulesById, setRulesById] = useState<Record<string, RecoveryRules>>({});
  const rulesFor = (id: string, brand?: string, batchName?: string) => rulesById[id] ?? defaultRulesForBrand(brand, batchName);
  const setRulesFor = (id: string, rules: RecoveryRules) => setRulesById(current => ({
    ...current,
    [id]: rules
  }));
  const resetRulesFor = (id: string) => setRulesById(current => {
    const {
      [id]: _removed,
      ...rest
    } = current;
    return rest;
  });
  // Guardrails open in a dialog so the user never leaves the inventory table.
  const [guardrailRow, setGuardrailRow] = useState<BatchRow | null>(null);
  const reduceMotion = useReducedMotion();
  // Clicking a forecast opens that upload's Carli report.
  const [reportRow, setReportRow] = useState<BatchRow | null>(null);
  const [sort, setSort] = useState<{
    key: SortKey;
    direction: 'asc' | 'desc';
  }>({
    key: 'uploaded',
    direction: 'desc'
  });
  const [page, setPage] = useState(1);
  useEffect(() => setDeselected([]), [selectedOwner]);
  useEffect(() => setPage(1), [selectedOwner, sort]);
  const toggleSort = (key: SortKey) => setSort(current => current.key === key ? {
    key,
    direction: current.direction === 'asc' ? 'desc' : 'asc'
  } : {
    key,
    direction: key === 'batch' || key === 'brand' ? 'asc' : 'desc'
  });
  const brands = useMemo(() => {
    const entries: {
      brand: string;
      batches: Batch[];
      base: BrandBase;
    }[] = [];
    if (includes('1 People')) entries.push({
      brand: '1 People',
      batches: onePeopleIncomingBatches,
      base: onePeopleBase
    });
    if (includes('Eddy')) entries.push({
      brand: 'Eddy',
      batches: eddyIncomingBatches,
      base: eddyBase
    });
    if (nisoloActive) {
      entries.push({
        brand: 'Nisolo',
        batches: nisoloSubmissions?.submissions ?? [],
        base: {
          receivedProducts: nisolo?.totalProducts ?? 0,
          receivedUnits: nisoloSubmissions?.totalReceived ?? 0,
          totalMsrp: nisoloSubmissions?.totalMsrp ?? 0,
          maxOpportunity: nisoloSubmissions?.totalEstimatedRecovery ?? 0,
          soldUnits: nisoloOrders?.units ?? 0,
          totalSales: nisoloOrders?.sales ?? 0
        }
      });
    }
    return entries;
  }, [selectedOwner, nisolo, nisoloSubmissions, nisoloOrders]);
  const unsortedRows: BatchRow[] = useMemo(
  // Batch names repeat within a brand, so the index keeps each row's id unique.
  () => brands.flatMap(({
    brand,
    batches
  }) => batches.map((batch, index) => ({
    ...batch,
    brand,
    id: submissionId(brand, index, batch.name)
  }))), [brands]);
  const rows: BatchRow[] = useMemo(() => {
    const direction = sort.direction === 'asc' ? 1 : -1;
    const value = (row: BatchRow) => {
      switch (sort.key) {
        case 'uploaded':
          return uploadedTime(row.arrived);
        case 'batch':
          return row.name.toLocaleLowerCase();
        case 'brand':
          return row.brand.toLocaleLowerCase();
        case 'units':
          return row.received;
        case 'msrp':
          return row.msrp ?? 0;
        case 'recovery':
          return row.estimatedRecovery ?? 0;
      }
    };
    return [...unsortedRows].sort((a, b) => {
      const left = value(a);
      const right = value(b);
      if (typeof left === 'string' || typeof right === 'string') return String(left).localeCompare(String(right)) * direction;
      return (left - right) * direction;
    });
  }, [unsortedRows, sort]);
  const pageCount = Math.max(1, Math.ceil(rows.length / ROWS_PER_PAGE));
  const currentPage = Math.min(page, pageCount);
  const pageRows = rows.slice((currentPage - 1) * ROWS_PER_PAGE, currentPage * ROWS_PER_PAGE);
  const isSelected = (row: BatchRow) => !deselected.includes(row.id);
  const toggleBatch = (row: BatchRow) => setDeselected(current => current.includes(row.id) ? current.filter(id => id !== row.id) : [...current, row.id]);
  const allSelected = deselected.length === 0;
  const toggleAll = () => setDeselected(allSelected ? rows.map(row => row.id) : []);

  // Each brand contributes only its selected submissions. Brands whose batch
  // records carry no valuation fall back to a share of their known totals.
  const totals = useMemo(() => {
    return brands.reduce((sum, {
      brand,
      batches,
      base
    }) => {
      const selected = batches.filter((batch, index) => !deselected.includes(`${brand}::${index}::${batch.name}`));
      const brandUnits = batches.reduce((count, batch) => count + batch.received, 0);
      const selectedUnits = selected.reduce((count, batch) => count + batch.received, 0);
      const share = batches.length === 0 ? 1 : brandUnits > 0 ? selectedUnits / brandUnits : selected.length / batches.length;
      const scale = (value: number) => Math.round(value * share);
      const lines = selected.reduce((count, batch) => count + (batch.lineItems ?? 0), 0);
      const batchMsrp = selected.reduce((value, batch) => value + (batch.msrp ?? 0), 0);
      const batchRecovery = selected.reduce((value, batch) => value + (batch.estimatedRecovery ?? 0), 0);
      const matched = brand === 'Nisolo' ? selected.reduce((result, submission) => {
        const outcome = reconcileSubmission(submission, nisoloOrders);
        return {
          units: result.units + outcome.soldUnits,
          sales: result.sales + outcome.actualSales
        };
      }, {
        units: 0,
        sales: 0
      }) : {
        units: scale(base.soldUnits),
        sales: scale(base.totalSales)
      };
      return {
        products: sum.products + (lines > 0 ? lines : scale(base.receivedProducts)),
        lines: sum.lines + lines,
        units: sum.units + (batches.length > 0 ? selectedUnits : base.receivedUnits),
        msrp: sum.msrp + (batchMsrp > 0 ? batchMsrp : scale(base.totalMsrp)),
        recovery: sum.recovery + (batchRecovery > 0 ? batchRecovery : scale(base.maxOpportunity)),
        soldUnits: sum.soldUnits + matched.units,
        totalSales: sum.totalSales + matched.sales
      };
    }, {
      products: 0,
      lines: 0,
      units: 0,
      msrp: 0,
      recovery: 0,
      soldUnits: 0,
      totalSales: 0
    });
  }, [brands, deselected, nisoloOrders]);
  const receivedProducts = totals.products;
  const receivedUnits = totals.units;
  const selectedLines = totals.lines;
  const predictedRecovery = totals.recovery;
  const perUnit = (value: number, units: number) => units > 0 ? (value / units).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2
  }) : '—';
  const summaryMetrics: SummaryMetric[] = [{
    label: 'Unit count',
    value: receivedUnits.toLocaleString('en-US'),
    detail: `Across ${receivedProducts.toLocaleString('en-US')} ${selectedLines > 0 ? 'variant lines' : 'products'}`
  }, {
    label: 'Total MSRP value',
    value: currency(totals.msrp),
    detail: 'Submitted retail value'
  }, {
    label: 'Predicted recovery',
    value: currency(predictedRecovery),
    detail: 'Carli estimate'
  }, {
    label: 'Predicted recovery / unit',
    value: perUnit(predictedRecovery, receivedUnits),
    detail: 'Per unit received'
  }];
  return <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8 lg:py-14" aria-labelledby="inventory-heading" data-id="element-1777" data-mp="3t3tba57tlgx6">
      <section className="flex flex-col gap-5 border-b border-ink/10 pb-8 sm:flex-row sm:items-end sm:justify-between" data-id="element-1778" data-mp="apk0jej60g2e">
        <div data-id="element-1779" data-mp="5vpkqm6okl8l">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#BB5E40]" data-id="element-1780" data-mp="kmf102z00qjb">
            <ArchiveIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1781" data-mp="fo52pjhz99th" />
            Inventory
          </span>
          <h1 id="inventory-heading" className="mt-3 font-display text-4xl font-medium tracking-tight sm:text-5xl" data-id="element-1782" data-mp="tm20gj863m9q">
            Inventory dashboard
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-ink/65 sm:text-lg" data-id="element-1783" data-mp="2vehv3cvq1qk4">
            A clear view of inventory received, prepared for sale, and currently live across your approved recovery channels.
          </p>
        </div>
        <span className="inline-flex w-fit items-center gap-2 rounded-full bg-forest/8 px-3 py-1.5 text-xs font-semibold text-forest" data-id="element-1784" data-mp="uda4r8uvpv0f">
          <span className="h-1.5 w-1.5 rounded-full bg-forest" data-id="element-1785" data-mp="zdftoh9rl3et" />
          {selectedOwner || 'All brands'}
        </span>
      </section>

      <div className="mt-6 flex gap-1 rounded-full border border-ink/10 bg-cream/60 p-1 w-fit" role="tablist" aria-label="Inventory sections" data-id="element-1786" data-mp="18cjcrl8xlitb">
        {(['Submissions', 'Units'] as const).map(tab => <button key={tab} type="button" role="tab" aria-selected={section === tab} onClick={() => setSection(tab)} className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${section === tab ? 'bg-[#BB5E40] text-cream shadow-sm' : 'text-ink/55 hover:text-ink'}`} data-id="element-1787" data-mp="1i9fmtvehaczz">
            {tab}
          </button>)}
      </div>

      {section === 'Units' ? <div className="mt-6" data-id="element-1788" data-mp="20ychi0dsf7iq">
          <UnitsDashboard selectedOwner={selectedOwner} embedded data-id="element-1789" data-mp="3lj971paft712" />
        </div> : <>
      <section className="mt-6" aria-label="Inventory overview" data-id="element-1790" data-mp="3gj4zioribmmz">
        <article className="rounded-3xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-7" aria-labelledby="summary-heading" data-id="element-1791" data-mp="3glzlwlu2zhj1">
          <div className="flex items-center gap-2" data-id="element-1792" data-mp="37jhy42ys2gtx">
            <BoxesIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1793" data-mp="37ksz5v0u9ahc" />
            <h2 id="summary-heading" className="text-lg font-semibold" data-id="element-1794" data-mp="82joz3j88lhr">Summary</h2>
          </div>
          <div className="mt-6 grid gap-x-5 gap-y-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-1795" data-mp="43j65l0r1iur">
            {summaryMetrics.map((metric, index) => <div key={metric.label} className={`border-[#BB5E40]/25 ${index === 0 ? 'pl-0' : 'sm:border-l-2 sm:pl-4'} ${index % 2 === 0 ? 'sm:border-l-0 sm:pl-0 lg:border-l-2 lg:pl-4' : ''} ${index === 0 ? 'lg:border-l-0 lg:pl-0' : ''}`} data-id="element-1796" data-mp="3vekowgh56ud1" data-mpi={index}>
                <p className="text-sm text-ink/55" data-id="element-1797" data-mp="n0u9bgpi4wfh">{metric.label}</p>
                <p className="mt-1 font-display text-3xl font-semibold tracking-tight text-ink" data-id="element-1798" data-mp="6ll2rsvjkbzy">{metric.value}</p>
                <p className="mt-1 text-xs text-ink/50" data-id="element-1799" data-mp="bl7d9ujzje52">{metric.detail}</p>
              </div>)}
          </div>
        </article>
      </section>

      <section className="mt-5" data-id="element-1800" data-mp="3tsdm12411hso">
        <article className="overflow-hidden rounded-2xl border border-ink/10 bg-paper" aria-labelledby="incoming-heading" data-id="element-1801" data-mp="2jpyqgm87kih">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-3 border-b border-ink/10 px-5 py-5 sm:px-6" data-id="element-1802" data-mp="qan1b2s4t5h2">
            <div className="flex items-center gap-2" data-id="element-1803" data-mp="u9vw8aywr9e7">
              <ClipboardListIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1804" data-mp="gk0rjp73j96a" />
              <h2 id="incoming-heading" className="text-base font-semibold" data-id="element-1805" data-mp="livkhjys2e44">Inventory submissions</h2>
            </div>
            {pageCount > 1 && <nav className="flex items-center gap-1.5 text-[13px]" aria-label="Submission pages" data-id="element-1806" data-mp="197o9ewgzflp8">
                <button type="button" onClick={() => setPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="rounded-lg border border-ink/15 p-1 text-ink/60 transition-colors hover:border-ink/30 hover:text-ink disabled:opacity-35 disabled:hover:border-ink/15 disabled:hover:text-ink/60" aria-label="Previous page" data-id="element-1807" data-mp="1dzy5yvclrk0h">
                  <ChevronLeftIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1808" data-mp="14f47ae22om7r" />
                </button>
                <span className="tabular-nums text-ink/60" data-id="element-1809" data-mp="10diokc2uxdpn">
                  Page <span className="font-semibold text-ink" data-id="element-1810" data-mp="10fbsvkpzzluh">{currentPage}</span> of {pageCount}
                </span>
                <button type="button" onClick={() => setPage(Math.min(pageCount, currentPage + 1))} disabled={currentPage === pageCount} className="rounded-lg border border-ink/15 p-1 text-ink/60 transition-colors hover:border-ink/30 hover:text-ink disabled:opacity-35 disabled:hover:border-ink/15 disabled:hover:text-ink/60" aria-label="Next page" data-id="element-1811" data-mp="1eaxfxmco7vk9">
                  <ChevronRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1812" data-mp="2wuc38j6qhz7g" />
                </button>
              </nav>}
            <p className="ml-auto text-[13px] text-ink/50" data-id="element-1813" data-mp="31sx5eo5jqsyj">{rows.length.toLocaleString('en-US')} submissions</p>
          </div>
          <div className="max-h-[420px] overflow-auto" data-id="element-1814" data-mp="296cbph1lm4rt">
            <table className="w-full min-w-[880px] text-left text-sm" data-id="element-1815" data-mp="297vv3gijwvxt">
              <thead className="sticky top-0 z-10 bg-cream text-xs text-ink/50" data-id="element-1816" data-mp="2e6727jhgoscc">
                <tr data-id="element-1817" data-mp="2j3yvohxr8min">
                  <th scope="col" className="py-3 pl-5 pr-2 font-semibold sm:pl-6" data-id="element-1818" data-mp="2j3gsf1cod818">
                    <span className="flex items-center gap-2.5" data-id="element-1819" data-mp="2nw0gis8znlka">
                      <SelectBox checked={allSelected} onChange={toggleAll} label="Select all submissions" data-id="element-1820" data-mp="1qfl0e9y1w4as" />
                      <SortButton label="Uploaded" sortKey="uploaded" sort={sort} onSort={toggleSort} data-id="element-1821" data-mp="1vee97p2e774m" />
                    </span>
                  </th>
                  <th scope="col" className="px-3 py-3 font-semibold" data-id="element-1822" data-mp="1nudinq11dd8t"><SortButton label="Batch" sortKey="batch" sort={sort} onSort={toggleSort} data-id="element-1823" data-mp="1iviqhxh6icc1" /></th>
                  {allBrands && <th scope="col" className="px-3 py-3 font-semibold" data-id="element-1824" data-mp="1dw639bmeb38x"><SortButton label="Brand" sortKey="brand" sort={sort} onSort={toggleSort} data-id="element-1825" data-mp="1dz1ygs6uzdto" /></th>}
                  <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-1826" data-mp="1908mx5b4tww5"><SortButton label="Submitted" sortKey="units" sort={sort} onSort={toggleSort} align="right" data-id="element-1827" data-mp="14x2ky9v1yrvh" /></th>
                  <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-1828" data-mp="1049enhjxrbha">Accepted</th>
                  <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-1829" data-mp="v6zep7yx7hf7">Acceptance rate</th>
                  <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-1830" data-mp="q8xvmn1hvi3q"><SortButton label="MSRP value" sortKey="msrp" sort={sort} onSort={toggleSort} align="right" data-id="element-1831" data-mp="l8jp59fjkg7t" /></th>
                  <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-1832" data-mp="h7ri8mu56nep"><SortButton label="Carli forecast" sortKey="recovery" sort={sort} onSort={toggleSort} align="right" data-id="element-1833" data-mp="253nvjgducfeh" /></th>
                  <th scope="col" className="px-5 py-3 text-right font-semibold sm:px-6" data-id="element-1834" data-mp="2a2qy8u6cm9ja">Guardrails</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/10" data-id="element-1835" data-mp="1v7b12gah1xio">
                {pageRows.length > 0 ? pageRows.map(row => {
                  const checked = isSelected(row);
                  const rules = rulesFor(row.id, row.brand, row.name);
                  const custom = !rulesMatchDefaults(rules, row.brand, row.name);
                  return <React.Fragment key={row.id} data-id="element-1836" data-mp="1cficcy2fqygr">
                    <tr className={checked ? '' : 'opacity-45'} data-id="element-1837" data-mp="1ccme18moe7rs">
                      <td className="whitespace-nowrap py-4 pl-5 pr-2 text-ink/60 sm:pl-6" data-id="element-1838" data-mp="1helhsjmbv6p9">
                        <span className="flex items-center gap-2.5" data-id="element-1839" data-mp="13f3phu9tpbb5">
                          <SelectBox checked={checked} onChange={() => toggleBatch(row)} label={`Include ${row.name} in totals`} data-id="element-1840" data-mp="17g5nk7s07hbj" />
                          {row.arrived}
                        </span>
                      </td>
                      <th scope="row" className="px-3 py-4 text-left font-medium text-ink" data-id="element-1841" data-mp="jtadartfui9o">{row.name}</th>
                      {allBrands && <td className="whitespace-nowrap px-3 py-4 text-ink/60" data-id="element-1842" data-mp="xtl4xx2ko0vw">{row.brand}</td>}
                      <td className="px-3 py-4 text-right font-semibold tabular-nums text-ink" data-id="element-1843" data-mp="srdrt460aerz">{row.received.toLocaleString('en-US')}</td>
                      <td className="px-3 py-4 text-right font-semibold tabular-nums text-ink" data-id="element-1844" data-mp="12r4jyz73ghow">{row.received.toLocaleString('en-US')}</td>
                      <td className="px-3 py-4 text-right tabular-nums text-ink/70" data-id="element-1845" data-mp="1ghge13n8oqio">100%</td>
                      <td className="px-3 py-4 text-right tabular-nums text-ink/70" data-id="element-1846" data-mp="1cgo5kcm6ypng">{row.msrp && row.msrp > 0 ? currency(row.msrp) : '—'}</td>
                      <td className="px-3 py-4 text-right" data-id="element-1847" data-mp="1qel43ek7h64r">
                        <ForecastCell row={row} onOpen={() => setReportRow(row)} data-id="element-1848" data-mp="1lino59cuu1si" />
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-right sm:px-6" data-id="element-1849" data-mp="1xz1938z8pc18">
                        <button type="button" onClick={() => setGuardrailRow(row)} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${custom ? 'border-[#BB5E40] bg-[#BB5E40]/8 text-[#94452E]' : 'border-ink/15 text-ink/60 hover:border-[#BB5E40]/40 hover:text-ink'}`} data-id="element-1850" data-mp="1t07utec4ph00">
                          <SlidersHorizontalIcon className="h-3 w-3" aria-hidden="true" data-id="element-1851" data-mp="1ozw9s0futeo0" />
                          {custom ? summarizeRules(rules) : 'See settings'}
                        </button>
                      </td>
                    </tr>
                    </React.Fragment>;
                }) : <tr data-id="element-1852" data-mp="rjquy1vevqzi">
                    <td colSpan={allBrands ? 9 : 8} className="px-5 py-10 text-center text-sm text-ink/55 sm:px-6" data-id="element-1853" data-mp="ri5yu7tg7zzj">No inventory submissions yet.</td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </article>
      </section>

      {nisoloActive && <section className="mt-5" aria-label="AI insights" data-id="element-1854" data-mp="2t7lb1wexzytc">
          <UploadAIInsights uploads={(nisoloSubmissions?.submissions ?? []).filter((submission, index) => !deselected.includes(`Nisolo::${index}::${submission.name}`))} orders={nisoloOrders} loading={submissionsLoading} error={submissionsError} data-id="element-1855" data-mp="2y9arjrkxp4ot" />
        </section>}
        </>}

      {reportRow && <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-4 sm:items-center" role="dialog" aria-modal="true" aria-label={`Carli report for ${reportRow.name}`} onClick={() => setReportRow(null)} data-id="element-1856" data-mp="3gy8yxr596z1j">
          <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-3xl border border-ink/10 bg-paper p-6 shadow-xl sm:p-8" onClick={event => event.stopPropagation()} data-id="element-1857" data-mp="c0v1y6mfdxe0">
            <div className="flex items-start justify-between gap-4" data-id="element-1858" data-mp="2ajo9scxw9tx">
              <div data-id="element-1859" data-mp="3thmcqc1e820g">
                <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-1860" data-mp="3ti5y62q958j8">
                  <CarliStars data-id="element-1861" data-mp="3pgsplkvryr8m" />
                  Carli forecast
                </span>
                <h2 className="mt-2 text-lg font-semibold" data-id="element-1862" data-mp="3kfwm38fc573q">{reportRow.name}</h2>
                <p className="mt-1 text-sm text-ink/55" data-id="element-1863" data-mp="3fhbnfdfqevhe">
                  {reportRow.brand} · {reportRow.received.toLocaleString('en-US')} units · {reportRow.estimatedRecovery ? currency(reportRow.estimatedRecovery) : '—'} forecast
                </p>
              </div>
              <button type="button" onClick={() => setReportRow(null)} aria-label="Close report" className="rounded-full p-2 text-ink/50 transition-colors hover:bg-cream hover:text-ink" data-id="element-1864" data-mp="1do45rxcnc1sc">
                <XIcon className="h-4 w-4" data-id="element-1865" data-mp="1l8g967druj1j" />
              </button>
            </div>

            <div className="mt-5 space-y-5" data-id="element-1866" data-mp="1q20y79xe02ol">
              <ConfidenceDetail row={reportRow} data-id="element-1867" data-mp="1q32bzm5sf3a0" />
              {reportRow.brand === 'Nisolo' && <UploadAIInsights uploads={(nisoloSubmissions?.submissions ?? []).filter(submission => submission.name === reportRow.name)} orders={nisoloOrders} loading={submissionsLoading} error={submissionsError} defaultOpen data-id="element-1868" data-mp="y9jpmgda091v" />}
            </div>
          </div>
        </div>}

      <AnimatePresence data-id="element-1869" data-mp="2ipdhnb893udo">
      {guardrailRow && <motion.div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-4 sm:items-center" role="dialog" aria-modal="true" aria-label={`Guardrails for ${guardrailRow.name}`} onClick={() => setGuardrailRow(null)} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.18,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-1870" data-mp="2iolqqdplmktq">
          <motion.div className="max-h-[88vh] w-full max-w-5xl overflow-y-auto rounded-3xl border border-ink/10 bg-paper p-6 shadow-xl sm:p-8" onClick={event => event.stopPropagation()} initial={reduceMotion ? {
          opacity: 0
        } : {
          opacity: 0,
          scale: 0.96,
          y: 18
        }} animate={reduceMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={reduceMotion ? {
          opacity: 0
        } : {
          opacity: 0,
          scale: 0.97,
          y: 10
        }} transition={{
          duration: 0.26,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-1871" data-mp="4h4c9du8thal">
            <motion.div className="flex items-start justify-between gap-4" initial={reduceMotion ? false : {
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.24,
            ease: [0.23, 1, 0.32, 1],
            delay: 0.06
          }} data-id="element-1872" data-mp="125nxmxkkjdyf">
              <div data-id="element-1873" data-mp="1lp3ia15hfcx3">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-1874" data-mp="16txab8mtt414">Guardrails</p>
                <h2 className="mt-1 text-lg font-semibold" data-id="element-1875" data-mp="1bsqdo88efenk">{guardrailRow.name}</h2>
                <p className="mt-1 text-sm text-ink/55" data-id="element-1876" data-mp="1butfdpiwyw84">
                  {guardrailRow.brand} · {guardrailRow.received.toLocaleString('en-US')} units uploaded {guardrailRow.arrived}
                </p>
                <p className="mt-2 text-xs font-medium text-ink/45" data-id="element-1877" data-mp="o3fm9hcxuwm3">View only</p>
                {onOpenSettings && <button type="button" onClick={() => {
                const id = guardrailRow.id;
                setGuardrailRow(null);
                onOpenSettings(id);
              }} className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-[#BB5E40] px-3 py-1.5 text-xs font-semibold text-[#94452E] transition-colors hover:bg-[#BB5E40]/8" data-id="element-1878" data-mp="t1z8livg2aez">
                    Go to Settings to change guardrails
                    <ArrowUpRightIcon className="h-3 w-3" aria-hidden="true" data-id="element-1879" data-mp="2c286dwn6g3ss" />
                  </button>}
              </div>
              <button type="button" onClick={() => setGuardrailRow(null)} aria-label="Close guardrails" className="rounded-full p-2 text-ink/50 transition-colors hover:bg-cream hover:text-ink" data-id="element-1880" data-mp="27094kr735exy">
                <XIcon className="h-4 w-4" data-id="element-1881" data-mp="2uq3hf8ren3i7" />
              </button>
            </motion.div>

            <motion.div className="mt-5" initial={reduceMotion ? false : {
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.28,
            ease: [0.23, 1, 0.32, 1],
            delay: 0.12
          }} data-id="element-1882" data-mp="2qpb9ciu9r4ni">
              <SubmissionRules batchName={guardrailRow.name} brand={guardrailRow.brand} canUnlock={isAdmin} readOnly rules={rulesFor(guardrailRow.id, guardrailRow.brand, guardrailRow.name)} onChange={next => setRulesFor(guardrailRow.id, next)} onReset={() => resetRulesFor(guardrailRow.id)} data-id="element-1883" data-mp="3qyszbzhnxfp1" />
            </motion.div>
          </motion.div>
        </motion.div>}
      </AnimatePresence>
    </div>;
}

// Forecast value plus Carli's confidence in it. Opens the upload's full report.
function ForecastCell({
  row,
  onOpen
}: {
  row: BatchRow;
  onOpen: () => void;
}) {
  const value = row.estimatedRecovery && row.estimatedRecovery > 0 ? currency(row.estimatedRecovery) : null;
  const confidence = scoreConfidence(row);
  if (!value) return <span className="text-sm text-ink/40" data-id="element-1884" data-mp="2odsi0zdwc0dk">—</span>;

  // Confidence reads green once Carli is more likely right than not, red below that.
  const tone = !confidence ? 'border-ink/15 bg-cream text-ink/60' : confidence.score >= 50 ? 'border-positive/30 bg-positive/8 text-positive' : 'border-[#BB5E40]/40 bg-[#BB5E40]/8 text-[#94452E]';
  return <button type="button" onClick={onOpen} className="group inline-flex flex-col items-end gap-1 rounded-lg px-1.5 py-1 transition-colors hover:bg-cream/70" aria-label={`Open Carli report for ${row.name}`} data-id="element-1885" data-mp="18r5h128gu37v">
      <span className="flex items-center gap-1.5 whitespace-nowrap font-semibold tabular-nums text-positive" data-id="element-1886" data-mp="g5m9aun2nlxn">
        {value}
        <ChevronRightIcon className="h-3.5 w-3.5 text-ink/35 transition-colors group-hover:text-[#BB5E40]" aria-hidden="true" data-id="element-1887" data-mp="q2ib6higt9t8" />
      </span>
      {confidence && <span className={`inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold tabular-nums ${tone}`} data-id="element-1888" data-mp="l58o38ijta8k">
          {confidence.label} · {confidence.score}%
        </span>}
    </button>;
}

// The breakdown behind an upload's confidence score, shown under its row.
function ConfidenceDetail({
  row
}: {
  row: BatchRow;
}) {
  const confidence = scoreConfidence(row);
  if (!confidence) return null;
  return <div className="rounded-2xl border border-[#BB5E40]/20 bg-cream/45 p-4 sm:p-5" data-id="element-1889" data-mp="35gaynt5wzu1p">
      <div className="flex flex-wrap items-center gap-2" data-id="element-1890" data-mp="3adauzccypifm">
        <CarliStars data-id="element-1891" data-mp="3afx9jvy9sy7c" />
        <p className="text-xs font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-1892" data-mp="1yiwjly2dtps">{confidence.label} confidence · {confidence.score}%</p>
        <span className="text-xs text-ink/55" data-id="element-1893" data-mp="5zcnypaptqnf">How much of {row.name} Carli could model when estimating recovery.</span>
      </div>
      <ul className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-5" data-id="element-1894" data-mp="3oc11tjpl2wh6">
        {confidence.factors.map(factor => <li key={factor.label} data-id="element-1895" data-mp="kxeth51b4m7b">
            <div className="flex items-baseline justify-between gap-2" data-id="element-1896" data-mp="kw3nqzoax4dk">
              <span className="text-xs font-semibold text-ink" data-id="element-1897" data-mp="podbqa5uvud7">{factor.label}</span>
              <span className="text-xs font-semibold tabular-nums text-ink/55" data-id="element-1898" data-mp="1rnvtgrm60bwj">{Math.round(factor.score * 100)}%</span>
            </div>
            <div className="mt-1.5 h-1 w-full rounded-full bg-ink/10" data-id="element-1899" data-mp="1wlxdpfvbuh9l">
              <div className="h-1 rounded-full bg-[#BB5E40]" style={{
            width: `${Math.round(factor.score * 100)}%`
          }} data-id="element-1900" data-mp="21dxkns9teafo" />
            </div>
            <p className="mt-1.5 text-[11px] leading-relaxed text-ink/55" data-id="element-1901" data-mp="25dzbf6e0hjg6">{factor.detail}</p>
          </li>)}
      </ul>
    </div>;
}
function SortButton({
  label,
  sortKey,
  sort,
  onSort,
  align = 'left'
}: {
  label: string;
  sortKey: SortKey;
  sort: {
    key: SortKey;
    direction: 'asc' | 'desc';
  };
  onSort: (key: SortKey) => void;
  align?: 'left' | 'right';
}) {
  const active = sort.key === sortKey;
  return <button type="button" onClick={() => onSort(sortKey)} aria-label={`Sort by ${label}`} className={`inline-flex items-center gap-1 font-semibold transition-colors hover:text-ink ${active ? 'text-ink' : 'text-ink/50'} ${align === 'right' ? 'flex-row-reverse' : ''}`} data-id="element-1902" data-mp="1mmomgurujgqx">
      {label}
      <ChevronUpIcon className={`h-3 w-3 shrink-0 transition-transform ${active ? 'opacity-100' : 'opacity-0'} ${active && sort.direction === 'desc' ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-1903" data-mp="389pl9yavabhc" />
    </button>;
}
function SelectBox({
  checked,
  onChange,
  label
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return <button type="button" role="checkbox" aria-checked={checked} aria-label={label} onClick={onChange} className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors ${checked ? 'border-[#BB5E40] bg-[#BB5E40] text-cream' : 'border-ink/25 bg-paper hover:border-[#BB5E40]/50'}`} data-id="element-1904" data-mp="2pi6kzz889bj0">
      {checked && <CheckIcon className="h-3 w-3" strokeWidth={3} aria-hidden="true" data-id="element-1905" data-mp="drkq0seim95v" />}
    </button>;
}